import { useEffect, useRef, useState } from "react";

/**
 * Animated number counter — triggers once when scrolled into view.
 * Props:
 *  - end: target number (e.g., 50, 500, 15000)
 *  - duration: ms (default 2000)
 *  - prefix / suffix: strings
 *  - format: "comma" | "none"
 */
export const Counter = ({
  end = 100,
  duration = 2000,
  prefix = "",
  suffix = "+",
  format = "comma",
  className = "",
  testId,
}) => {
  const [value, setValue] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !started) {
            setStarted(true);
          }
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - startTime) / duration);
      // ease out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(end * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [started, end, duration]);

  const formatted =
    format === "comma" ? value.toLocaleString("en-IN") : String(value);

  return (
    <span ref={ref} data-testid={testId} className={`tabular-nums ${className}`}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
};

export default Counter;
